/**
 * Peptide Calculator — Premium Dark Clinical JS Component
 *
 * ── Formula ─────────────────────────────────────────────────
 *   concentration (mg/ml) = peptide_mg / water_ml
 *   volume_ml             = dose_mg / concentration
 *   syringe_units         = volume_ml × 100  (U-100 insulin: 1ml = 100 units)
 *
 * ── Features ────────────────────────────────────────────────
 *   • Result display: units / ml / both
 *   • Progress rail: dots + connecting lines update per step
 *   • Number counter animation on result
 *   • Syringe SVG: glow filter, gradient fill, glass highlight,
 *     bigger tick labels (16px bold), SMIL spring animation,
 *     glowing target marker arrow
 */

class PeptideCalculator {
  /** @param {HTMLElement} el — [data-calculator] wrapper */
  constructor(el) {
    this.el     = el;
    this.id     = el.dataset.calculator;
    this.config = (window.__pcConfig && window.__pcConfig[this.id]) || {};

    this.state = {
      syringe:      null,   // ml
      syringeUnits: null,   // unit cap (30 / 50 / 100)
      peptide:      null,   // mg
      water:        null,   // ml
      dose:         null,   // mg (always normalised)
    };

    this.customMode = { peptide: false, water: false, dose: false };

    this._prevResult = null; // for counter animation deduplication

    this._init();
  }

  // ── Init ─────────────────────────────────────────────────────

  _init() {
    this._staggerButtons();
    this._bindOptionButtons();
    this._bindCustomInputs();
    this._bindImageProtection();
    this._showPlaceholder();
    this._updateRail(); // mark step 1 as "active" by default
  }

  _staggerButtons() {
    this.el.querySelectorAll('.pc__option-btn').forEach((btn, i) => {
      btn.style.setProperty('--pc-btn-delay', `${i * 35}ms`);
    });
  }

  _bindOptionButtons() {
    this.el.querySelectorAll('.pc__option-btn').forEach((btn) => {
      btn.addEventListener('click', () => this._handleOptionClick(btn));
    });
  }

  _bindCustomInputs() {
    this.el.querySelectorAll('.pc__input[data-field]').forEach((input) => {
      input.addEventListener('input', () => this._calculate());
    });
    const doseUnit = this.el.querySelector('[data-field="dose-unit"]');
    if (doseUnit) doseUnit.addEventListener('change', () => this._calculate());
  }

  // ── Media protection (basic anti-save UX) ────────────────────
  _bindImageProtection() {
    const protectedSelector = '.pc__option-img, .pc__step-img, .pc__syringe-diagram svg, .pc__syringe-diagram img';

    // Disable drag-to-desktop and native image drag behavior.
    this.el.querySelectorAll(protectedSelector).forEach((node) => {
      node.setAttribute('draggable', 'false');
    });

    this.el.addEventListener('dragstart', (e) => {
      if (e.target && e.target.closest && e.target.closest(protectedSelector)) {
        e.preventDefault();
      }
    });

    // Block typical right-click save action on protected media only.
    this.el.addEventListener('contextmenu', (e) => {
      if (e.target && e.target.closest && e.target.closest(protectedSelector)) {
        e.preventDefault();
      }
    });
  }

  // ── Option click ──────────────────────────────────────────────

  _handleOptionClick(btn) {
    const field = btn.dataset.field;
    const value = btn.dataset.value;

    // Deselect siblings
    this.el.querySelectorAll(`.pc__option-btn[data-field="${field}"]`).forEach((b) => {
      b.classList.remove('pc__option-btn--selected');
      b.setAttribute('aria-pressed', 'false');
    });
    btn.classList.add('pc__option-btn--selected');
    btn.setAttribute('aria-pressed', 'true');

    if (value === 'other') {
      this.customMode[field] = true;
      this._showCustomInput(field);
      if (field === 'syringe') {
        this.state.syringe = null;
        this.state.syringeUnits = null;
      } else {
        this.state[field] = null;
      }
    } else {
      this.customMode[field] = false;
      this._hideCustomInput(field);
      if (field === 'syringe') {
        this.state.syringe      = parseFloat(value);
        this.state.syringeUnits = parseInt(btn.dataset.units, 10);
      } else {
        this.state[field] = parseFloat(value);
      }
    }

    // Mark this step as done
    const stepEl = btn.closest('.pc__step');
    if (stepEl) stepEl.classList.add('pc__step--done');

    this._updateRail();
    this._calculate();
  }

  // ── Progress rail ─────────────────────────────────────────────

  /** Update the progress rail nodes + connecting lines based on state */
  _updateRail() {
    const section = this.el.closest('section') || this.el;

    const done = [
      !!(this.state.syringe || this.customMode.syringe),
      !!(this.state.peptide || (this.customMode.peptide && this._getCustomPeptide() > 0)),
      !!(this.state.water   || (this.customMode.water   && this._getCustomWater()   > 0)),
      !!(this.state.dose    || (this.customMode.dose    && this._getCustomDose()     > 0)),
    ];

    // Find the first incomplete step (becomes "active")
    let firstIncomplete = done.indexOf(false);
    if (firstIncomplete === -1) firstIncomplete = 3; // all done

    for (let i = 1; i <= 4; i++) {
      const node = section.querySelector(`[data-rail-node="${i}"]`);
      const line = section.querySelector(`[data-rail-line="${i}"]`);
      if (!node) continue;

      node.classList.toggle('pc__rail-node--done',   done[i - 1]);
      node.classList.toggle('pc__rail-node--active', !done[i - 1] && i - 1 === firstIncomplete);

      if (line) {
        const fill = line.querySelector('.pc__rail-line-fill');
        if (fill) fill.style.width = done[i - 1] ? '100%' : '0%';
      }
    }
  }

  // ── Custom input visibility ───────────────────────────────────

  _showCustomInput(field) {
    const w = this.el.querySelector(`[data-custom-for="${field}"]`);
    if (!w) return;
    w.hidden = false;
    const inp = w.querySelector('input');
    if (inp) setTimeout(() => inp.focus(), 50);
  }

  _hideCustomInput(field) {
    const w = this.el.querySelector(`[data-custom-for="${field}"]`);
    if (!w) return;
    w.hidden = true;
    const inp = w.querySelector('input');
    if (inp) inp.value = '';
  }

  // ── Custom value readers ──────────────────────────────────────

  _getCustomPeptide() {
    const inp = this.el.querySelector('[data-field="peptide-custom"]');
    return inp ? parseFloat(inp.value) : NaN;
  }

  _getCustomWater() {
    const inp = this.el.querySelector('[data-field="water-custom"]');
    return inp ? parseFloat(inp.value) : NaN;
  }

  _getCustomDose() {
    const inp    = this.el.querySelector('[data-field="dose-custom"]');
    const unitEl = this.el.querySelector('[data-field="dose-unit"]');
    if (!inp) return NaN;
    const raw  = parseFloat(inp.value);
    if (isNaN(raw)) return NaN;
    const unit = unitEl ? unitEl.value : 'mg';
    return unit === 'mcg' ? raw / 1000 : raw;
  }

  // ── Core calculation ──────────────────────────────────────────

  _calculate() {
    const syringeUnits = this.state.syringeUnits;
    const syringe      = this.state.syringe;

    const peptideMg = this.customMode.peptide ? this._getCustomPeptide() : this.state.peptide;
    const waterMl   = this.customMode.water   ? this._getCustomWater()   : this.state.water;
    const doseMg    = this.customMode.dose    ? this._getCustomDose()    : this.state.dose;

    if (
      !syringe || !syringeUnits ||
      !peptideMg || isNaN(peptideMg) || peptideMg <= 0 ||
      !waterMl   || isNaN(waterMl)   || waterMl   <= 0 ||
      !doseMg    || isNaN(doseMg)    || doseMg    <= 0
    ) {
      this._showPlaceholder();
      return;
    }

    const concentration = peptideMg / waterMl;
    const volumeMl      = doseMg / concentration;
    const unitsNeeded   = volumeMl * 100;

    const dp           = this._dp();
    const unitsDisplay = parseFloat(unitsNeeded.toFixed(dp));
    const mlDisplay    = parseFloat(volumeMl.toFixed(Math.min(dp + 1, 4)));

    if (unitsNeeded > syringeUnits) {
      this._showWarning(unitsDisplay, mlDisplay, syringeUnits, syringe);
    } else {
      const doseLabel = this._formatDose(doseMg);
      this._showResult(doseLabel, unitsDisplay, mlDisplay, syringeUnits, syringe);
    }
  }

  // ── Helpers ───────────────────────────────────────────────────

  _dp() { return Math.max(0, Math.min(4, parseInt(this.config.decimalPlaces ?? 1, 10))); }

  _formatDose(mg) {
    if (mg < 1) return `${parseFloat((mg * 1000).toPrecision(4))} mcg`;
    return `${parseFloat(mg.toPrecision(6))} mg`;
  }

  _buildResultValue(units, ml, suffix) {
    const mode = this.config.resultDisplayMode || 'units';
    if (mode === 'ml')   return `<strong class="pc__result-number--pop">${ml} ml</strong>`;
    if (mode === 'both') return `<strong class="pc__result-number--pop">${units} ${suffix} / ${ml} ml</strong>`;
    return `<strong class="pc__result-number--pop">${units} ${suffix}</strong>`;
  }

  // ── UI toggles ────────────────────────────────────────────────

  _showPlaceholder() {
    this._q('[data-placeholder]').hidden = false;
    this._q('[data-warning]').hidden     = true;
    this._q('[data-result]').hidden      = true;
  }

  _showWarning(units, ml, syringeUnits, syringeMl) {
    const warningEl = this._q('[data-warning]');
    const textEl    = this._q('[data-warning-text]');
    const base      = this.config.warningText || 'Warning: The required volume exceeds your selected syringe capacity.';
    textEl.textContent = base;

    this._q('[data-placeholder]').hidden = true;
    this._q('[data-result]').hidden      = true;
    warningEl.hidden = false;

    // Re-trigger shake
    const inner = warningEl.querySelector('.pc__warning-inner');
    if (inner) { inner.style.animation = 'none'; void inner.offsetWidth; inner.style.animation = ''; }
  }

  _showResult(doseLabel, units, ml, syringeUnits, syringeMl) {
    const resultEl = this._q('[data-result]');
    const textEl   = this._q('[data-result-text]');
    const prefix   = this.config.resultPrefix || 'For a dose of';
    const middle   = this.config.resultMiddle || 'pull the syringe to';
    const suffix   = this.config.resultSuffix || 'units';

    textEl.innerHTML =
      `${prefix} <strong>${doseLabel}</strong>, ${middle} ${this._buildResultValue(units, ml, suffix)}`;

    // Syringe diagram
    const diagramEl = this._q('[data-syringe-diagram]');
    if (diagramEl && this.config.showSyringeVisual) {
      diagramEl.innerHTML = this._buildSyringeSVG(units, syringeUnits, ml, syringeMl);
    }

    this._q('[data-placeholder]').hidden = true;
    this._q('[data-warning]').hidden     = true;
    resultEl.hidden = false;

    // Re-trigger entrance animation
    resultEl.style.animation = 'none';
    void resultEl.offsetWidth;
    resultEl.style.animation = '';
  }

  // ── Syringe SVG ───────────────────────────────────────────────

  /**
   * Premium syringe diagram:
   *   • SVG defs: glow filter, gradient fill, glass highlight
   *   • Wider viewBox (580×140) for big readable tick labels
   *   • Tick labels: 16px bold — matches reference site standards
   *   • SMIL dual-animate fill (x + width) with spring easing
   *   • Animated glowing target arrow appears after fill
   *   • Realistic plunger rubber tip shape
   *   • Glass highlight stripe at top of fill
   *
   * @param {number} units       — required units
   * @param {number} maxUnits    — syringe capacity in units
   * @param {number} ml          — required ml
   * @param {number} maxMl       — syringe capacity in ml
   */
  _buildSyringeSVG(units, maxUnits, ml, maxMl) {
    const cs          = getComputedStyle(this.el);
    const bodyColor   = cs.getPropertyValue('--pc-syringe-body').trim()    || '#1A3050';
    const fillColor   = cs.getPropertyValue('--pc-syringe-fill').trim()    || '#38BDF8';
    const outline     = cs.getPropertyValue('--pc-syringe-outline').trim() || '#4A8BAA';
    const accentColor = cs.getPropertyValue('--pc-accent-color').trim()    || '#38BDF8';
    const mode        = this.config.resultDisplayMode || 'units';

    // Unique IDs per section instance
    const uid      = this.id.replace(/[^a-z0-9]/gi, '');
    const clipId   = `pc-bc-${uid}`;
    const gradFill = `pc-gf-${uid}`;
    const gradGlow = `pc-gg-${uid}`;
    const glowFilt = `pc-gl-${uid}`;
    const hlClip   = `pc-hl-${uid}`;

    // ── Geometry
    const VW = 580, VH = 140;

    // Barrel
    const bX1 = 36,  bX2 = 420;
    const bY1 = 24,  bY2 = 76;
    const bW  = bX2 - bX1;
    const bH  = bY2 - bY1;
    const bRx = 5;

    // Inner liquid zone
    const iX1 = bX1 + 4, iX2 = bX2 - 2;
    const iY1 = bY1 + 4, iY2 = bY2 - 4;
    const iW  = iX2 - iX1;
    const iH  = iY2 - iY1;

    // Left thumb cap
    const capMid = (bY1 + bY2) / 2;
    const capHH  = 28;

    // Needle cone
    const nConeX = bX2;
    const nTipX  = 568;
    const nY     = capMid;
    const nHalfH = 16;

    // ── Fill
    const ratio  = Math.min(1, Math.max(0, units / maxUnits));
    const fillW  = Math.round(iW * ratio);
    const fillX  = iX2 - fillW;

    // Plunger rubber tip
    const plX    = Math.max(iX1, fillX - 3);

    // ── Tick configuration
    const tickStep = maxUnits <= 30 ? 5 : maxUnits <= 50 ? 10 : 20;
    const ticks = [];
    for (let v = 0; v <= maxUnits; v += tickStep) {
      const tx = iX2 - (v / maxUnits) * iW;
      ticks.push({ v, tx, major: true });
    }
    // Minor half-step ticks
    for (let v = tickStep / 2; v < maxUnits; v += tickStep) {
      const tx = iX2 - (v / maxUnits) * iW;
      ticks.push({ v, tx, major: false });
    }

    const tickLabel = (v) => {
      if (mode === 'ml') {
        return parseFloat(((v / maxUnits) * maxMl).toFixed(2)).toString();
      }
      return v.toString();
    };

    const scaleUnit = mode === 'ml' ? 'ml' : 'units';

    return `<svg
  viewBox="0 0 ${VW} ${VH}"
  xmlns="http://www.w3.org/2000/svg"
  role="img"
  aria-label="Syringe: pull to ${units} of ${maxUnits} ${scaleUnit}">

  <defs>
    <!-- Blur glow filter on fill -->
    <filter id="${glowFilt}" x="-20%" y="-50%" width="140%" height="200%">
      <feGaussianBlur stdDeviation="4" result="blur"/>
      <feMerge>
        <feMergeNode in="blur"/>
        <feMergeNode in="SourceGraphic"/>
      </feMerge>
    </filter>

    <!-- Clip to inner barrel -->
    <clipPath id="${clipId}">
      <rect x="${iX1}" y="${iY1}" width="${iW}" height="${iH}" rx="2"/>
    </clipPath>

    <!-- Clip for glass highlight (top third of inner barrel) -->
    <clipPath id="${hlClip}">
      <rect x="${iX1}" y="${iY1}" width="${iW}" height="${Math.round(iH * 0.38)}"/>
    </clipPath>

    <!-- Vertical gradient on fill: bright → rich -->
    <linearGradient id="${gradFill}" x1="0" y1="0" x2="0" y2="1">
      <stop offset="0%"   stop-color="${fillColor}" stop-opacity="0.9"/>
      <stop offset="100%" stop-color="${fillColor}" stop-opacity="1"/>
    </linearGradient>

    <!-- Horizontal glow overlay at edges of fill -->
    <linearGradient id="${gradGlow}" x1="0" y1="0" x2="1" y2="0">
      <stop offset="0%"   stop-color="${fillColor}" stop-opacity="0.5"/>
      <stop offset="40%"  stop-color="${fillColor}" stop-opacity="0"/>
      <stop offset="100%" stop-color="${fillColor}" stop-opacity="0.3"/>
    </linearGradient>
  </defs>

  <!-- ── Barrel background (empty) -->
  <rect
    x="${bX1}" y="${bY1}" width="${bW}" height="${bH}"
    rx="${bRx}"
    fill="${bodyColor}"
    stroke="${outline}" stroke-width="1.5"
  />

  ${fillW > 0 ? `
  <!-- ── Animated fill (liquid) with SMIL spring easing -->
  <rect
    x="${iX2}" y="${iY1}" width="0" height="${iH}"
    fill="url(#${gradFill})"
    clip-path="url(#${clipId})"
    filter="url(#${glowFilt})"
  >
    <animate attributeName="x"
      from="${iX2}" to="${fillX}"
      dur="0.75s" fill="freeze"
      calcMode="spline" keyTimes="0;1"
      keySplines="0.16 1 0.3 1"/>
    <animate attributeName="width"
      from="0" to="${fillW}"
      dur="0.75s" fill="freeze"
      calcMode="spline" keyTimes="0;1"
      keySplines="0.16 1 0.3 1"/>
  </rect>

  <!-- ── Glass highlight: white stripe at top of fill -->
  <rect
    x="${fillX}" y="${iY1}" width="${fillW}" height="${Math.round(iH * 0.38)}"
    fill="rgba(255,255,255,0.14)"
    clip-path="url(#${clipId})"
  >
    <animate attributeName="x"
      from="${iX2}" to="${fillX}"
      dur="0.75s" fill="freeze"
      calcMode="spline" keyTimes="0;1"
      keySplines="0.16 1 0.3 1"/>
    <animate attributeName="width"
      from="0" to="${fillW}"
      dur="0.75s" fill="freeze"
      calcMode="spline" keyTimes="0;1"
      keySplines="0.16 1 0.3 1"/>
  </rect>

  <!-- ── Horizontal glow overlay on fill -->
  <rect
    x="${fillX}" y="${iY1}" width="${fillW}" height="${iH}"
    fill="url(#${gradGlow})"
    clip-path="url(#${clipId})"
    opacity="0.6"
  >
    <animate attributeName="x"
      from="${iX2}" to="${fillX}"
      dur="0.75s" fill="freeze"
      calcMode="spline" keyTimes="0;1"
      keySplines="0.16 1 0.3 1"/>
    <animate attributeName="width"
      from="0" to="${fillW}"
      dur="0.75s" fill="freeze"
      calcMode="spline" keyTimes="0;1"
      keySplines="0.16 1 0.3 1"/>
  </rect>` : ''}

  <!-- ── Barrel outline (on top of fill for clean edge) -->
  <rect
    x="${bX1}" y="${bY1}" width="${bW}" height="${bH}"
    rx="${bRx}" fill="none"
    stroke="${outline}" stroke-width="1.5"
  />

  <!-- ── Tick marks (major + minor) -->
  ${ticks.map(({ v, tx, major }) => {
    const tickLen = major ? 15 : 8;
    return `<line
    x1="${tx}" y1="${bY2}"
    x2="${tx}" y2="${bY2 + tickLen}"
    stroke="${outline}"
    stroke-width="${major ? 1.5 : 0.8}"
    stroke-linecap="round"
  />`;
  }).join('\n  ')}

  <!-- ── Tick labels: 16px bold — large and clear like reference sites -->
  ${ticks.filter(t => t.major).map(({ v, tx }) => `
  <text
    x="${tx}" y="${bY2 + 32}"
    text-anchor="middle"
    font-size="16"
    font-weight="800"
    font-family="${this._fontStack()}"
    fill="${outline}"
    letter-spacing="-0.02em"
  >${tickLabel(v)}</text>`).join('')}

  <!-- ── Scale unit label -->
  <text
    x="${(iX1 + iX2) / 2}" y="${bY2 + 52}"
    text-anchor="middle"
    font-size="11"
    font-weight="600"
    font-family="${this._fontStack()}"
    fill="${outline}"
    opacity="0.5"
    letter-spacing="0.05em"
    text-transform="uppercase"
  >${scaleUnit.toUpperCase()}</text>

  <!-- ── Plunger rubber tip (realistic shape) -->
  ${fillW > 0 ? `
  <rect
    x="${plX}" y="${iY1 - 2}"
    width="8" height="${iH + 4}"
    rx="3"
    fill="${outline}"
    opacity="0.9"
  />
  <!-- rubber tip inner highlight -->
  <rect
    x="${plX + 2}" y="${iY1 + 2}"
    width="2" height="${iH - 4}"
    rx="1"
    fill="rgba(255,255,255,0.2)"
  />` : `
  <!-- Plunger fully pushed in (empty) -->
  <rect
    x="${iX2 - 8}" y="${iY1 - 2}"
    width="8" height="${iH + 4}"
    rx="3"
    fill="${outline}" opacity="0.9"
  />`}

  <!-- Plunger rod -->
  <rect
    x="${Math.max(bX1 + 6, plX - 30)}" y="${capMid - 2}"
    width="${Math.min(30, plX - bX1 - 6)}" height="4"
    fill="${outline}" opacity="0.6" rx="2"
  />

  <!-- ── Left cap (thumb rest) -->
  <!-- outer flange -->
  <rect
    x="0" y="${capMid - capHH}"
    width="${bX1}" height="${capHH * 2}"
    rx="4"
    fill="${bodyColor}"
    stroke="${outline}" stroke-width="1.5"
  />
  <!-- inner detail -->
  <rect
    x="6" y="${capMid - capHH + 8}"
    width="${bX1 - 12}" height="${capHH * 2 - 16}"
    rx="2"
    fill="rgba(255,255,255,0.05)"
  />

  <!-- ── Needle cone -->
  <polygon
    points="${nConeX},${nY - nHalfH} ${nTipX},${nY} ${nConeX},${nY + nHalfH}"
    fill="${bodyColor}"
    stroke="${outline}" stroke-width="1.5"
    stroke-linejoin="round"
  />

  <!-- ── Needle shaft -->
  <rect
    x="${nTipX}" y="${nY - 1.5}"
    width="${VW - nTipX}" height="3"
    fill="${outline}" rx="1.5"
  />

  <!-- ── Glowing target arrow (appears after fill animation) -->
  ${fillW > 0 ? `
  <g opacity="0">
    <animate attributeName="opacity"
      from="0" to="1"
      begin="0.7s" dur="0.35s" fill="freeze"
      calcMode="spline" keyTimes="0;1"
      keySplines="0.16 1 0.3 1"/>

    <!-- vertical dashed line at the target -->
    <line
      x1="${fillX + 4}" y1="${bY1 - 8}"
      x2="${fillX + 4}" y2="${bY2 + 20}"
      stroke="${accentColor}" stroke-width="2"
      stroke-dasharray="4 3"
      opacity="0.8"
    />

    <!-- downward arrow above barrel -->
    <polygon
      points="${fillX - 3},${bY1 - 18} ${fillX + 11},${bY1 - 18} ${fillX + 4},${bY1 - 6}"
      fill="${accentColor}"
    />

    <!-- glowing dot at needle-side of fill -->
    <circle cx="${iX2 - 2}" cy="${capMid}" r="4"
      fill="${accentColor}" opacity="0.9">
      <animate attributeName="r"
        values="4;6;4" dur="2s" repeatCount="indefinite"/>
      <animate attributeName="opacity"
        values="0.9;0.4;0.9" dur="2s" repeatCount="indefinite"/>
    </circle>
  </g>` : ''}

</svg>`;
  }

  /** Font stack for SVG text */
  _fontStack() {
    const f = getComputedStyle(this.el)
      .getPropertyValue('--pc-btn-font')
      .trim()
      .replace(/"/g, "'");
    return f || "Outfit, system-ui, sans-serif";
  }

  /** @param {string} sel @returns {Element|null} */
  _q(sel) { return this.el.querySelector(sel); }
}

// ── Auto-init ─────────────────────────────────────────────────

function initPeptideCalculators() {
  document.querySelectorAll('[data-calculator]').forEach((el) => {
    if (!el.__pcInstance) el.__pcInstance = new PeptideCalculator(el);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initPeptideCalculators);
} else {
  initPeptideCalculators();
}

// ── Shopify Theme Editor live-reload ──────────────────────────

if (window.Shopify && window.Shopify.designMode) {
  document.addEventListener('shopify:section:load', (e) => {
    const el = e.target.querySelector('[data-calculator]');
    if (el && !el.__pcInstance) el.__pcInstance = new PeptideCalculator(el);
  });
  document.addEventListener('shopify:section:reorder', initPeptideCalculators);
}
